import { BaseReactComponent, MuiPaletteColor } from '../../../types';
import { Badge, BadgeProps, SvgIconProps } from '@mui/material';
import styled from 'styled-components/macro';
import classnames from 'classnames';

export interface HiBadgeProps extends BaseReactComponent {
  /**
   * The badge will be added relative to this node.
   */
  children?: BadgeProps['children'];
  /**
   * The content rendered within the badge.
   */
  content?: BadgeProps['badgeContent'] | React.ComponentType<SvgIconProps>;
  /**
   * The color of the component. It supports basic theme colors.
   * @default primary
   */
  color?: MuiPaletteColor | 'light';
  /**
   * The variant to use.
   */
  variant?: BadgeProps['variant'] | 'outlined' | 'icon';
  /**
   * Override Mui Badge props
   * (https://mui.com/material-ui/api/badge/).
   */
  BadgeProps?: BadgeProps;
}

/**
 * The Badge component is used to display an information, usually a number of items (e.g. number of selected accounts).
 *
 * Use the Badge to draw attention to another interface element or to display a notification.
 * It "stamps" another piece of UI, such as an Avatar or a text title.
 *
 * It extends Mui's `Badge` component.
 */
const HiBadge = ({
  className,
  children,
  content,
  color,
  variant = 'standard',
  BadgeProps,
  ...other
}: HiBadgeProps) => {
  const IconComponent =
    variant === 'icon'
      ? (content as React.ComponentType<SvgIconProps>)
      : undefined;

  return (
    <StyledHiBadge
      {...other}
      {...BadgeProps}
      className={classnames(className, {
        'HiBadge-variant-nochild': !children,
        'HiBadge-variant-icon': variant === 'icon',
        'HiBadge-variant-outlined': variant === 'outlined'
      })}
      color={color}
      badgeContent={
        IconComponent ? (
          <IconComponent className="HiBadge-iconComponent" />
        ) : typeof content === 'number' ? (
          `${content}`
        ) : (
          content
        )
      }
      variant={variant === 'dot' ? 'dot' : 'standard'}
      classes={{
        badge: 'HiBadge-badge'
      }}
    >
      {children}
    </StyledHiBadge>
  );
};

const StyledHiBadge = styled(Badge).attrs(({ color = 'primary' }) => ({
  color: color
}))`
  &.HiBadge-variant-nochild {
    .HiBadge-badge {
      position: relative;
      transform-origin: unset;
      transform: unset;
    }
  }

  &.HiBadge-variant-icon {
    .HiBadge-badge {
      width: 20px;
    }
    .HiBadge-iconComponent {
      font-size: 16px;
    }
  }

  &.HiBadge-variant-outlined {
    .HiBadge-badge {
      color: ${({ color, theme }) => theme.palette[color].dark};
      background-color: #fff;
      border: solid 1px ${({ color, theme }) => theme.palette[color].main};
    }
  }
`;

export default HiBadge;
